import { ref, computed } from 'vue'
import { BaseRoleService } from '@/services/roles/BaseRoleService'

/**
 * P13-V7B (refutation) — `role` is a CANONICAL role name (the shell passes the session's). The legacy
 * alias table this composable used to translate through (`useRolePermissions.js`) is deleted: a legacy
 * spelling now selects no card and no producer, instead of being translated into one.
 */
export const useRoleKPIs = (role) => {
  const canonicalRole = String(role || '')
  const kpis = ref([])
  const loading = ref(false)
  const error = ref(null)
  
  let roleService = null

  const initializeService = () => {
    if (!roleService) {
      roleService = new BaseRoleService(role)
    }
  }

  const loadKPIs = async (params = {}, options = {}) => {
    try {
      loading.value = true
      error.value = null
      initializeService()
      
      // Pasar opción de deshabilitar caché (útil para actualizaciones forzadas)
      const kpiData = await roleService.getKPIs(params, { disableCache: options.forceRefresh || false })
      
      console.log('📊 [useRoleKPIs] Datos recibidos del backend:', kpiData)
      
      // Formatear KPIs según el rol
      kpis.value = formatKPIsByRole(kpiData, canonicalRole)
      
      console.log('📊 [useRoleKPIs] KPIs formateados:', kpis.value)
      
      return kpis.value
    } catch (err) {
      error.value = err.message || 'Error cargando KPIs del rol'
      console.warn('⚠️ Error en useRoleKPIs:', err.message)
      
      const fallbackKPIs = getFallbackKPIsForRole(canonicalRole)
      kpis.value = fallbackKPIs
      
      return fallbackKPIs
    } finally {
      loading.value = false
    }
  }

  /**
   * ── P13-V5A-R5 · A KPI CARD IS PUBLISHED ONLY IF THE SERVER STATED ITS VALUE ──
   *
   * Three distinct defects met in this one function, and all three published business facts
   * the server never produced:
   *
   *  1. NO ANSWER RENDERED AS AN ANSWER. `BaseRoleService.getKPIs` catches its own transport
   *     failure and returns `{ status: 'NOT_AVAILABLE' }` — it never throws. So the honest
   *     `getFallbackKPIsForRole` path below was DEAD CODE, and the live path fell straight
   *     through to the table, where every `|| ` turned an undefined field into a figure. Only
   *     `route_operator` was fenced; every other role published.
   *  2. THE SUBSTITUTES THEMSELVES. `data.system_health || '100%'` reported a perfectly
   *     healthy system precisely when the request had failed; `data.total_users || 0`,
   *     `data.tasks_pending || 0` and `data.attendance_status || 'Inactivo'` asserted counts
   *     and an attendance STATE the server never reported.
   *  3. LABEL vs PRODUCER. `field_supervisor` read `data.agencies_count` and
   *     `data.active_routes`, and GET /api/analytics/zone-kpis emits NEITHER key
   *     (RealAnalyticsReadService.js:446-472 emits `total_agencies`, `operational_agencies`,
   *     `route_completion`, …). So even a fully successful, correctly scoped read rendered
   *     "Agencias Gestionadas 0" — a real measurement silently replaced by a literal.
   *
   * The rule now: an unanswered read publishes NOTHING (the route_operator fence, generalised
   * — we cannot know a value we never received), and an answered read publishes each field
   * only as the producer stated it, with an explicit absence where it did not.
   */
  const NO_ANSWER_STATES = ['NOT_AVAILABLE', 'NO_PRODUCER']
  const isUnanswered = (data) =>
    !data || typeof data !== 'object' || NO_ANSWER_STATES.includes(data.status) || data.unavailable === true

  /** Explicit absence of a single field within an otherwise valid answer. */
  const ABSENT = 'Sin datos'

  const formatKPIsByRole = (data, currentRole) => {
    if (isUnanswered(data)) {
      // Two different absences, and they must not look alike (§10):
      //   NO_PRODUCER      the capability does not exist for this role — announcing it as
      //                    "unavailable" would imply it otherwise would be. Publish nothing.
      //   NOT_AVAILABLE    a real producer exists and the read failed — say so, because
      //                    silence here is indistinguishable from "this role has no KPIs".
      if (data?.status === 'NO_PRODUCER') return []
      return getFallbackKPIsForRole(currentRole)
    }

    const baseKPIs = Array.isArray(data.kpis) ? data.kpis : []

    // KPIs específicos por rol — only for roles with a VERIFIED producer
    // (see BaseRoleService.getEndpointsByRole). `super_admin`, `agency_employee`,
    // `administration_manager` and `department_head` have none, so they publish no card here
    // rather than a substituted one; department_head's real KPIs live in its own dashboard.
    /**
     * ── P13-V5A-R6 · A CLIENT PERMISSION MAY NARROW A CARD, BUT NOT ERASE A SERVED FACT ──
     *
     * Both `field_supervisor` cards declared `permission: 'agencies.view'` — a string held by NO
     * role in `useRolePermissions` and referenced nowhere else in the repository. `RoleKpiCard`
     * masks the value of a card whose permission the actor lacks (`RoleKpiCard.vue:101` returns
     * `'---'`), so these two cards rendered their TITLE and then `---` for the only role that can
     * ever receive them, on every load, while `GET /api/analytics/zone-kpis` answered 200 with the
     * real `total_agencies` / `operational_agencies` for that actor's authorised partition.
     *
     * R5 repaired which FIELD these cards read; nothing asserted the value reached the screen,
     * because the frontend suites format the KPI list without mounting the card in a permission
     * context. The composed product therefore withheld a capability its canonical producer had
     * already authorised and scoped — the mirror image of the fabrications V5A removes, and the
     * same "repaired in source, inert in the app" shape as R5's realtime bridge.
     *
     * The gate is REMOVED rather than satisfied by minting a grant: the read already passed
     * `authorizeRoles([... 'field_supervisor'])` + the UGA∩administration partition at the
     * producer, and these cards exist only inside the `field_supervisor` branch. A second,
     * client-side authority over a fact the server already released to this actor is exactly the
     * parallel authority this vertical keeps removing. Cards whose permission its role DOES hold
     * (the three `route_operator` cards below) are untouched — they are the positive control.
     */
    /**
     * ── W2 · REPORTING-PAYLOAD-EXCESS-API-ANALYTICS · THE CARDS NOW READ SOLD FACTS ──
     *
     * These two cards read `total_agencies` and `operational_agencies` from
     * `GET /api/analytics/zone-kpis`. R3 classified both as UNCONTRACTED_CAPABILITY — agency-estate
     * counts the signed first-pilot Reporting clause does not name — and the backend projection now
     * removes them from that response.
     *
     * They are REPLACED, not emptied. Leaving "Agencias Gestionadas" rendering `'Sin datos'` would
     * keep advertising a capability nobody bought while implying it merely has no value today; §19
     * calls that the wrong repair. The right one is to publish facts the SAME signed route still
     * returns, so the supervisor's strip stays useful after the narrowing: `pending_incidents` and
     * `resolved_today` are both INCIDENCE_KPI_ALLOWED under the clause.
     *
     * The `?? ABSENT` semantics are unchanged, and that distinction is load-bearing: a real 0 is an
     * observation and renders as 0, while a field the producer did not emit renders as an explicit
     * absence. No invented fallback, no second authority, no new endpoint.
     */
    const roleSpecificKPIs = {
      field_supervisor: [
        {
          key: 'pending_incidents',
          title: 'Incidencias Pendientes',
          value: data.pending_incidents ?? ABSENT,
          icon: 'mdi-clipboard-alert',
          color: 'orange'
        },
        {
          key: 'resolved_today',
          title: 'Resueltas Hoy',
          value: data.resolved_today ?? ABSENT,
          icon: 'mdi-check-circle',
          color: 'green'
        }
      ],
      // P13-V7B (refutation): the `permission` keys these three cards carried are gone with the card's
      // mask and the permission table they were checked against. The cards are also unreachable today:
      // BaseRoleService declares no KPI producer for the route operator (WS5 · K1), so NO_PRODUCER
      // publishes zero cards before this table is read.
      route_operator: [
        {
          key: 'route_progress',
          title: 'Progreso de Ruta',
          value: data.route_progress ?? 'Sin datos',
          icon: 'mdi-progress-check',
          color: 'green'
        },
        {
          key: 'visits_today',
          title: 'Visitas Hoy',
          value: data.visits_today ?? 'Sin datos',
          icon: 'mdi-map-marker-check',
          color: 'blue'
        },
        {
          key: 'performance',
          title: 'Rendimiento',
          value: data.performance_today ?? 'Sin datos',
          icon: 'mdi-trending-up',
          color: 'warning',
          trend: data.performance_comparison ? {
            percentage: data.performance_comparison,
            type: data.performance_trend || 'stable'
          } : null,
          subtitle: data.performance_yesterday ? `vs ${data.performance_yesterday} ayer` : null
        }
      ],
      // agency_employee — RETIRED in P13-V5A-R5. Its producer `/api/agency/employee-kpis` was
      // withdrawn on a binding decision of George (agencyStateRoutes.js:392), so there is no
      // reader behind these cards at all. They published `data.attendance_status || 'Inactivo'`
      // — asserting that a person was NOT AT WORK because a request had failed — and
      // `data.tasks_pending || 0`. Note the shell never rendered them either
      // (BaseRoleDashboard.vue:97 excludes agency_employee from the KPI row), so this removes a
      // fabrication that was already invisible; nothing a user could see is withdrawn.
      department_head: [
        // KPIs eliminados - el dashboard tiene sus propios KPIs específicos
      ]
    }

    // Combinar KPIs base con específicos del rol
    const specificKPIs = roleSpecificKPIs[currentRole] || []
    return [...baseKPIs, ...specificKPIs]
  }

  const getFallbackKPIsForRole = (currentRole) => {
    const fallbackKPIs = {
      route_operator: [],
      default: [
        {
          key: 'status',
          title: 'Estado del Sistema',
          value: 'No disponible',
          icon: 'mdi-wifi-off',
          color: 'grey',
          description: 'No se pudieron cargar los datos'
        }
      ]
    }

    return fallbackKPIs[currentRole] || fallbackKPIs.default
  }

  const refreshKPIs = async (params = {}) => {
    return await loadKPIs(params)
  }

  const roleKPIs = computed(() => kpis.value)
  const isLoading = computed(() => loading.value)
  const hasError = computed(() => !!error.value)

  return {
    roleKPIs,
    loading: isLoading,
    error,
    hasError,
    loadKPIs,
    refreshKPIs
  }
}
